import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Camera, Hammer, ListChecks, GitPullRequestArrow, ClipboardCheck, DollarSign, CheckCircle2, Clock, ImageIcon } from "lucide-react";

const STAGES = ["Demolition", "Rough Plumbing", "Rough Electrical", "Framing", "Waterproofing", "Tile", "Flooring", "Fixtures & Vanity", "Glass", "Paint & Trim", "Punch List"];
const PHOTO_CATS = ["during", "issues", "walkthrough", "after"];

export default function ProjectMode() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [wtOpen, setWtOpen] = useState(false);

  const load = async () => { const { data } = await api.get(`/projects/${projectId}`); setP(data); };
  useEffect(() => { load(); }, [projectId]);

  const setStatus = async (status, note = "") => {
    try { await api.post(`/projects/${projectId}/status`, { status, note }); toast.success("Updated"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decideChange = async (cid, approved) => {
    try { await api.patch(`/projects/${projectId}/changes/${cid}`, { approved }); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <AppShell><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;

  const pct = p.completion_pct || 0;
  const photosByCat = (cat) => (p.photos || []).filter((x) => x.category === cat);

  return (
    <AppShell title="Active Project">
      <div className="p-4 sm:p-6 pb-20">
        <button data-testid="project-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Dashboard</button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold">{p.customer_name}</h1>
            <div className="text-slate-500">{p.address}{p.estimate && <> · <span className="text-emerald-400 font-mono-data">${p.estimate.amount.toLocaleString()}</span></>}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={p.status} />
            {p.status === "sold" && <Button data-testid="start-construction-button" onClick={() => setStatus("active_project")} className="bg-blue-500 hover:bg-blue-600 text-white font-bold"><Hammer className="w-4 h-4 mr-2" />Start Construction</Button>}
            {p.status === "active_project" && <Button data-testid="mark-completed-button" onClick={() => setStatus("completed")} className="bg-slate-600 hover:bg-slate-500 text-white font-bold"><CheckCircle2 className="w-4 h-4 mr-2" />Mark Completed</Button>}
          </div>
        </div>

        {/* sales outcome */}
        {["price_ready", "estimate_presented", "follow_up"].includes(p.status) && (
          <div className="rounded-2xl bg-slate-900 border border-orange-500/30 p-4 mb-6" data-testid="sales-outcome-card">
            <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-2">Sales Outcome</div>
            {p.estimate && <div className="text-3xl font-display font-extrabold text-emerald-400 mb-3 font-mono-data">${p.estimate.amount.toLocaleString()}<span className="text-xs font-normal text-slate-500 ml-2">approved price</span></div>}
            <div className="flex flex-wrap gap-2">
              {p.status === "price_ready" && <Button data-testid="mark-presented-button" onClick={() => setStatus("estimate_presented")} className="bg-teal-500 hover:bg-teal-600 text-slate-950 font-bold">Estimate Presented</Button>}
              <Button data-testid="mark-sold-button" onClick={() => setStatus("sold")} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold"><CheckCircle2 className="w-4 h-4 mr-2" />Mark SOLD</Button>
              <Button data-testid="mark-followup-button" variant="outline" onClick={() => setStatus("follow_up")} className="border-slate-700 text-slate-300 hover:bg-slate-800">Follow-Up</Button>
              <Button data-testid="mark-lost-button" variant="outline" onClick={() => setStatus("lost")} className="border-slate-700 text-rose-300 hover:bg-slate-800">Lost</Button>
            </div>
          </div>
        )}

        {/* completion bar */}
        {["sold", "active_project", "completed"].includes(p.status) && (
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-400">Overall Completion{p.active_stage ? ` · ${p.active_stage}` : ""}</span>
            <span className="font-mono-data font-bold text-blue-300">{pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden"><motion.div className="h-full bg-blue-500" initial={{ width: 0 }} animate={{ width: `${pct}%` }} /></div>
        </div>
        )}

        <Tabs defaultValue="timeline">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-project-photos">Photos</TabsTrigger>
            <TabsTrigger value="changes" data-testid="tab-changes">Changes</TabsTrigger>
            <TabsTrigger value="walkthrough" data-testid="tab-walkthrough">Walkthrough</TabsTrigger>
          </TabsList>

          {/* Timeline */}
          <TabsContent value="timeline" className="mt-5">
            <div className="relative pl-6 border-l-2 border-slate-800 space-y-5">
              {[...(p.timeline || [])].reverse().map((t) => (
                <div key={t.id} className="relative" data-testid={`timeline-entry-${t.id}`}>
                  <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-orange-500 border-4 border-slate-950" />
                  <div className="text-sm font-medium">{t.label}</div>
                  <div className="text-xs text-slate-500">{t.by} · {new Date(t.at).toLocaleString()}</div>
                </div>
              ))}
              {(p.timeline || []).length === 0 && <div className="text-slate-500 text-sm">No activity yet.</div>}
            </div>
          </TabsContent>

          {/* Progress */}
          <TabsContent value="progress" className="mt-5">
            <Button data-testid="add-progress-button" onClick={() => setProgressOpen(true)} className="mb-4 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold"><Hammer className="w-4 h-4 mr-2" />Add Progress Update</Button>
            <div className="space-y-3">
              {[...(p.progress_updates || [])].reverse().map((u) => (
                <div key={u.id} className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold text-orange-300">{u.stage || "Update"}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(u.at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-200">{u.note}</p>
                  {u.completion_pct > 0 && <div className="text-xs text-blue-300 mt-1 font-mono-data">{u.completion_pct}% complete</div>}
                </div>
              ))}
              {(p.progress_updates || []).length === 0 && <div className="text-slate-500 text-sm">No progress updates yet.</div>}
            </div>
          </TabsContent>

          {/* Photos */}
          <TabsContent value="photos" className="mt-5">
            <Button data-testid="add-project-photo-button" onClick={() => setPhotoOpen(true)} className="mb-4 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold"><Camera className="w-4 h-4 mr-2" />Add Photo</Button>
            {["before", ...PHOTO_CATS].map((cat) => {
              const ph = photosByCat(cat);
              if (ph.length === 0) return null;
              return (
                <div key={cat} className="mb-6">
                  <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-2 capitalize">{cat === "before" ? "Before (Field)" : cat}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {ph.map((x) => (
                      <div key={x.id} className="rounded-xl overflow-hidden border border-slate-800 bg-slate-900">
                        <img src={fileUrl(x.storage_path)} alt={cat} className="w-full h-32 object-cover" />
                        {x.note && <div className="text-xs text-slate-500 p-1.5">{x.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {(p.photos || []).length === 0 && <div className="text-slate-500 text-sm">No photos yet.</div>}
          </TabsContent>

          {/* Changes */}
          <TabsContent value="changes" className="mt-5">
            <Button data-testid="add-change-button" onClick={() => setChangeOpen(true)} className="mb-4 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold"><GitPullRequestArrow className="w-4 h-4 mr-2" />New Change Request</Button>
            <div className="space-y-3">
              {[...(p.change_requests || [])].reverse().map((c) => (
                <div key={c.id} className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold">{c.request}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.approved === true ? "bg-emerald-500/20 text-emerald-300" : c.approved === false ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>
                      {c.approved === true ? "Approved" : c.approved === false ? "Declined" : "Pending Office"}
                    </span>
                  </div>
                  {c.description && <p className="text-sm text-slate-300">{c.description}</p>}
                  <div className="text-xs text-slate-500 mt-1">Scope: {c.scope_impact || "TBD"} · Price: {c.price_impact || "TBD"}</div>
                  {c.approved == null && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" data-testid={`approve-change-${c.id}`} onClick={() => decideChange(c.id, true)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 h-8">Approve</Button>
                      <Button size="sm" variant="outline" data-testid={`decline-change-${c.id}`} onClick={() => decideChange(c.id, false)} className="border-slate-700 text-slate-300 h-8">Decline</Button>
                    </div>
                  )}
                </div>
              ))}
              {(p.change_requests || []).length === 0 && <div className="text-slate-500 text-sm">No change requests.</div>}
            </div>
          </TabsContent>

          {/* Walkthrough */}
          <TabsContent value="walkthrough" className="mt-5">
            <Button data-testid="open-walkthrough-button" onClick={() => setWtOpen(true)} className="mb-4 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold"><ClipboardCheck className="w-4 h-4 mr-2" />{p.walkthrough ? "Update" : "Start"} Final Walkthrough</Button>
            {p.walkthrough ? (
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-3 max-w-2xl">
                <div><div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-1">Punch List</div>
                  {(p.walkthrough.punch_list || []).length === 0 ? <div className="text-sm text-slate-500">None</div> : <ul className="text-sm space-y-1">{p.walkthrough.punch_list.map((x, i) => <li key={i} className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-slate-600" />{x}</li>)}</ul>}</div>
                <div><div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-1">Customer Concerns</div><div className="text-sm text-slate-200">{p.walkthrough.customer_concerns || "None"}</div></div>
                <div className="flex items-center gap-4 text-sm">
                  <span>Acceptance: <span className="font-semibold capitalize">{p.walkthrough.customer_acceptance || "pending"}</span></span>
                  <span>Review requested: <span className="font-semibold">{p.walkthrough.review_requested ? "Yes" : "No"}</span></span>
                </div>
              </div>
            ) : <div className="text-slate-500 text-sm">No walkthrough recorded yet.</div>}
          </TabsContent>
        </Tabs>
      </div>

      <ProgressDialog open={progressOpen} onClose={() => setProgressOpen(false)} projectId={projectId} onDone={load} initialPct={pct} />
      <ChangeDialog open={changeOpen} onClose={() => setChangeOpen(false)} projectId={projectId} onDone={load} />
      <ProjectPhotoDialog open={photoOpen} onClose={() => setPhotoOpen(false)} projectId={projectId} onDone={load} />
      <WalkthroughDialog open={wtOpen} onClose={() => setWtOpen(false)} projectId={projectId} onDone={load} existing={p.walkthrough} />
    </AppShell>
  );
}

function ProgressDialog({ open, onClose, projectId, onDone, initialPct }) {
  const [stage, setStage] = useState(STAGES[0]);
  const [note, setNote] = useState("");
  const [pct, setPct] = useState(initialPct || 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setNote(""); setPct(initialPct || 0); } }, [open, initialPct]);
  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await api.post(`/projects/${projectId}/progress`, { stage, note, completion_pct: pct }); toast.success("Progress added"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Progress Update</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">Stage</Label>
            <Select value={stage} onValueChange={setStage}><SelectTrigger data-testid="progress-stage-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-64">{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-slate-400 text-xs">Note</Label>
            <Textarea data-testid="progress-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Plumbing rough-in completed today." className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div><Label className="text-slate-400 text-xs">Overall completion: {pct}%</Label>
            <Slider value={[pct]} onValueChange={(v) => setPct(v[0])} max={100} step={5} className="mt-3" /></div>
        </div>
        <DialogFooter><Button data-testid="progress-save-button" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeDialog({ open, onClose, projectId, onDone }) {
  const [f, setF] = useState({ request: "", description: "", scope_impact: "", price_impact: "" });
  useEffect(() => { if (open) setF({ request: "", description: "", scope_impact: "", price_impact: "" }); }, [open]);
  const save = async () => {
    if (!f.request.trim()) return;
    try { await api.post(`/projects/${projectId}/changes`, f); toast.success("Change request created — flagged for office"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Change Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">Customer request</Label><Input data-testid="change-request-input" value={f.request} onChange={(e) => setF({ ...f, request: e.target.value })} placeholder="e.g. Add heated flooring" className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div><Label className="text-slate-400 text-xs">Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-slate-400 text-xs">Scope impact</Label><Input value={f.scope_impact} onChange={(e) => setF({ ...f, scope_impact: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Price impact</Label><Input value={f.price_impact} onChange={(e) => setF({ ...f, price_impact: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" /></div>
          </div>
          <p className="text-[11px] text-slate-500">This is flagged for office review and does not authorize the change.</p>
        </div>
        <DialogFooter><Button data-testid="change-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectPhotoDialog({ open, onClose, projectId, onDone }) {
  const [category, setCategory] = useState("during");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef();
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", category); fd.append("note", note);
      await api.post(`/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added"); setNote(""); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Add Project Photo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="project-photo-category" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{PHOTO_CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-slate-400 text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
          <input ref={fileRef} data-testid="project-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          <Button data-testid="project-photo-capture" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5 mr-2" />Capture / Choose</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WalkthroughDialog({ open, onClose, projectId, onDone, existing }) {
  const [punch, setPunch] = useState("");
  const [concerns, setConcerns] = useState("");
  const [acceptance, setAcceptance] = useState("pending");
  const [review, setReview] = useState(false);
  useEffect(() => { if (open) { setPunch((existing?.punch_list || []).join("\n")); setConcerns(existing?.customer_concerns || ""); setAcceptance(existing?.customer_acceptance || "pending"); setReview(existing?.review_requested || false); } }, [open, existing]);
  const save = async () => {
    try {
      await api.post(`/projects/${projectId}/walkthrough`, { punch_list: punch.split("\n").map((x) => x.trim()).filter(Boolean), customer_concerns: concerns, customer_acceptance: acceptance, review_requested: review });
      toast.success("Walkthrough saved"); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Final Walkthrough</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">Punch list (one per line)</Label><Textarea data-testid="walkthrough-punch-input" value={punch} onChange={(e) => setPunch(e.target.value)} rows={4} placeholder="Touch up grout at niche&#10;Adjust shower door alignment" className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div><Label className="text-slate-400 text-xs">Customer concerns</Label><Textarea value={concerns} onChange={(e) => setConcerns(e.target.value)} rows={2} className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div><Label className="text-slate-400 text-xs">Customer acceptance</Label>
            <Select value={acceptance} onValueChange={setAcceptance}><SelectTrigger data-testid="walkthrough-acceptance-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100"><SelectItem value="pending">Pending</SelectItem><SelectItem value="accepted">Accepted</SelectItem></SelectContent></Select></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input data-testid="walkthrough-review-checkbox" type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} className="w-4 h-4 accent-orange-500" /> Request customer review (syncs to HighLevel)</label>
        </div>
        <DialogFooter><Button data-testid="walkthrough-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Save Walkthrough</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
